import type {
  IconSpec,
  SearchFilterValue,
  SearchHitAnchor,
  SearchHitAnchorEnvelope,
  SearchRecentSnapshot,
  SearchScope,
} from '@hibro/shared';
import { scopeCovers } from '@hibro/shared';

import { BUILTIN_COMMAND } from '../commands/builtin-commands';

/**
 * Global search contract (架构方案 §5.9 / 见 03 header 槽位).
 *
 * The kernel here does filtering, ranking and delivery; it holds no layout
 * decision. Which sections a face draws, how many rows each shows, what the
 * empty state says and whether a tab strip exists are all the host's calls —
 * the same line `queryCommands` draws for commands, generalised to every kind
 * of result.
 */

/**
 * Top-level scope of one search.
 *
 * Two levels on purpose (需求 FR-14 §5.3.2): the tab axis is **per application**
 * (All, then one per app), and only *inside* an app tab do results split by the
 * sections that app registered (IM's Messages / Files, AI's 对话 / 图片). Today
 * only `all` exists — no module can register a search provider yet — but the
 * shape is fixed now so adding one is a new provider rather than a refactor.
 */
export type SearchTab = { kind: 'all' } | { kind: 'app'; moduleId: string };

/**
 * Which tab a provider answers under, mirroring the manifest's
 * {@link SearchScope}. Absent means the provider's own default: platform
 * providers only exist on `all`, module ones default to `app`.
 */
export type ProviderScope = SearchScope;

/**
 * Section a result belongs to. Under the `all` tab these are the two platform
 * constants below plus any module section whose `scope` covers `all`, in the
 * order that section declared for this tab; under an app tab the ids come from
 * that app's registration and the platform does not read their meaning.
 */
export type SearchSection = string;

/** Recently searched-and-opened results (见 `recent-provider`). */
export const SECTION_RECENT = 'recent';
/** Commands, including the synthesised `/open <module>` rows. */
export const SECTION_COMMAND = 'command';

/** Section order under the `all` tab (需求 §4.2.1 分组顺序). */
export const ALL_TAB_SECTIONS: readonly SearchSection[] = [
  SECTION_RECENT,
  SECTION_COMMAND,
];

/**
 * Merge weight per built-in provider — **plain configuration**, which is the
 * point: the order of two providers inside one section is a number declared
 * here, not a rule like "is this the platform's open provider" hard-coded into
 * the merge. Lower sorts first; a provider added later picks its own number.
 *
 * `/open <module>` comes before declared commands because opening an app is the
 * most common thing typed into the box, and it would otherwise lose to any
 * command whose label happens to score alike.
 */
export const PROVIDER_RANK = {
  openCommand: 0,
  command: 1,
  recent: 0,
  /** Module sections: after the platform's own, offset by declaration order. */
  moduleSection: 10,
} as const;

/** The prefix that puts the box in command mode (需求 §4.2.1). */
export const COMMAND_PREFIX = '/';

/**
 * One request to open the search box with `text` already in the field.
 *
 * This is how the two built-in search commands differ and the only thing that
 * differs between them: `workbench.commandPalette` seeds {@link COMMAND_PREFIX}
 * and so lands in command mode, `workbench.search` seeds nothing and shows the
 * empty state. A single entry point could not serve both — whichever prefix it
 * chose made the other mode unreachable from the keyboard.
 *
 * Carries a token instead of being compared by value: the text repeats (`/`
 * every time), and the request has to take effect even when the box is already
 * open, which is exactly the case a value comparison would swallow.
 */
export interface SearchSeed {
  readonly text: string;
  /** Bumped per request, so a repeated seed still applies. */
  readonly token: number;
  /**
   * App tab to select, as a module id. Absent means leave the current tab
   * alone: `mod+p` and the header field open the box without moving it.
   */
  readonly tabModuleId?: string;
}

/**
 * Module id a `workbench.search` argument asked to land on.
 *
 * Anything other than `{ moduleId: string }` is "no tab", including a missing
 * argument — the chord and the header field pass none, and must not move the
 * strip.
 */
export function readSearchTabModuleId(arg: unknown): string | undefined {
  if (!arg || typeof arg !== 'object') {
    return undefined;
  }
  const moduleId = (arg as { moduleId?: unknown }).moduleId;
  return typeof moduleId === 'string' && moduleId.length > 0
    ? moduleId
    : undefined;
}

/**
 * The tab a seed asks to select. `undefined` means the request did not name
 * one, so the face keeps whatever tab is already showing.
 */
export function searchTabFromSeed(
  seed: Pick<SearchSeed, 'tabModuleId'> | undefined,
): SearchTab | undefined {
  const moduleId = seed?.tabModuleId;
  return moduleId ? { kind: 'app', moduleId } : undefined;
}

/**
 * One search, already parsed. Providers receive this rather than a raw string
 * so the prefix and tokenisation are decided once.
 */
export interface SearchQuery {
  /** Exactly what the user typed, prefix included. */
  readonly raw: string;
  /** Set when {@link raw} opens with a recognised mode prefix. */
  readonly prefix?: string;
  /**
   * {@link raw} minus the prefix, trimmed but **case preserved** — the query as
   * the user means it. This is what goes to a provider that does its own
   * matching (a module's search engine); handing it {@link terms} instead would
   * silently lowercase the input, and an engine with a case-sensitive index then
   * finds nothing.
   */
  readonly text: string;
  /**
   * {@link text} lowercased and split on whitespace, for the platform's own
   * substring matching. Providers that delegate matching should use `text`.
   */
  readonly terms: readonly string[];
  readonly tab: SearchTab;
  /**
   * Most rows the caller wants from each provider. A hint, not a cap the face
   * relies on: truncation is presentation (see {@link SearchResult}), and this
   * exists so a slow provider is not asked to fetch hundreds of rows the header
   * dropdown would throw away. Omitted means "everything you have".
   */
  readonly limit?: number;
  /**
   * Filter values the user set on the active app tab, keyed by the declaring
   * module's filter id — already pruned of the ones carrying no constraint.
   *
   * The kernel routes these and reads none of them; only the owning module's
   * providers ever see them (filters live on an app tab, which is that one
   * module's scope).
   */
  readonly filters?: Readonly<Record<string, SearchFilterValue>>;
}

/**
 * What choosing a result does.
 *
 * Two declarative members plus `run` as the escape hatch, deliberately not
 * "just a closure": a provider that ends up living in the extension host has to
 * be able to *describe* its action rather than hand over a function. The
 * launch-backed providers use `run` today because opening a launchable goes
 * through `createLaunchActivator`, which needs the whole item (the detached,
 * `resolveInstance` and external-focus branches are not expressible as data).
 */
export type SearchAction =
  | {
      readonly type: 'command';
      readonly command: string;
      /**
       * Arguments forwarded to the command. Only module-contributed hits use
       * them: a section's row says "open conversation X" by pointing at the
       * module's own public command (见 `ModuleSearchAction`).
       */
      readonly args?: readonly unknown[];
      /**
       * Show something beside this row instead of navigating: the dropdown
       * stays open and the row's viewport rect rides along as a trailing
       * argument (见 `ModuleSearchAction.inPlace`).
       */
      readonly inPlace?: boolean;
    }
  | {
      readonly type: 'openView';
      readonly viewId: string;
      readonly instanceKey?: string;
    }
  | { readonly type: 'run'; readonly run: () => void };

/**
 * One row, prepared for display. Carries no grouping, truncation or ordering
 * weight beyond {@link score} — see the note at the top of this file.
 */
export interface SearchResult {
  /** Unique within the provider; `${providerId}:${key}` is globally unique. */
  readonly key: string;
  readonly providerId: string;
  readonly section: SearchSection;
  /** Primary line. Query terms are highlighted by the face, not marked here. */
  readonly title: string;
  /**
   * Secondary line: context path, command description, `@username`.
   * The face highlights query terms here the same way it does on {@link title}.
   */
  readonly subtitle?: string;
  readonly icon?: IconSpec;
  /**
   * Small tag drawn **beside the title**: the owning module, a command's
   * category, a message's date. It qualifies the name, which is why it sits
   * next to it rather than at the row's far edge.
   */
  readonly badge?: string;
  /**
   * When this row names a person. The face does not interpret it; a loaded
   * module UI may adorn the title. Copied onto the recent snapshot.
   */
  readonly personId?: string;
  /**
   * Right-aligned secondary label. For a command this is the `/spelling` you
   * would type to reach it, which is why it is not the subtitle: what the row
   * *is* (its description) and how you *reach* it are different things, and the
   * description is the one worth reading first.
   */
  readonly trailing?: string;
  /** Key chord for the underlying command, when one is bound (见 §5.9). */
  readonly keybinding?: string;
  /** Relevance within the provider; higher sorts first. */
  readonly score: number;
  readonly action: SearchAction;
  /**
   * Set when this row **stands for a row from somewhere else** — the recent
   * section, whose rows are replays of earlier picks.
   *
   * It is what gets recorded when the row is chosen (见 {@link recentIdentityOf}).
   * Without it, re-picking a recent row recorded `providerId: 'recent'` against
   * the recent section's own composed key: an entry pointing at itself, which no
   * provider can resolve, so it sat in the store forever crowding out real ones
   * (20-entry cap) while never rendering.
   */
  readonly origin?: {
    readonly providerId: string;
    readonly key: string;
  };
}

/** The identity to record for a pick — the row's own, or the one it replays. */
export function recentIdentityOf(result: SearchResult): {
  providerId: string;
  key: string;
} {
  return result.origin ?? { providerId: result.providerId, key: result.key };
}

/**
 * The snapshot to store with a pick, or `undefined` when the row needs none.
 *
 * Only declarative actions can be replayed from disk, and that is exactly the
 * right condition: a `run` closure belongs to a platform provider, and those
 * resolve live anyway (见 `SearchRecentEntry`). So "can I store this?" and
 * "does this need storing?" have the same answer, and no caller has to know
 * which providers are which.
 */
export function recentSnapshotOf(
  result: SearchResult,
): SearchRecentSnapshot | undefined {
  if (result.action.type === 'run') {
    return undefined;
  }
  return {
    title: result.title,
    ...(result.subtitle === undefined ? {} : { subtitle: result.subtitle }),
    ...(result.icon === undefined ? {} : { icon: result.icon }),
    ...(result.badge === undefined ? {} : { badge: result.badge }),
    ...(result.personId === undefined ? {} : { personId: result.personId }),
    action: result.action,
  };
}

/**
 * A source of results.
 *
 * `search` may be synchronous — the three built-ins are, since they read state
 * the renderer already holds — but the signature allows a promise because every
 * future provider crosses a process boundary.
 */
export interface SearchProvider {
  readonly id: string;
  /** Section its results land in. */
  readonly section: SearchSection;
  /**
   * Owning application. Absent for the platform's own providers, which is what
   * keeps them out of an app tab: `all` runs every provider, an app tab runs
   * only the ones that app registered (see {@link providerAnswers}).
   */
  readonly moduleId?: string;
  /**
   * Mode prefixes it answers under. Omitted means "only the prefix-less mode";
   * list {@link COMMAND_PREFIX} to also answer in command mode.
   */
  readonly prefixes?: readonly string[];
  /**
   * Which tab this provider answers under. Only meaningful together with
   * {@link moduleId} — a platform provider has no app tab to appear on.
   * Defaults to `app` for module providers (见 {@link providerAnswers}).
   */
  readonly scope?: ProviderScope;
  /**
   * Merge weight within a section; lower sorts first. Lets a fallback row (the
   * future "ask AI about …") sit last without the face special-casing it.
   */
  readonly rank: number;
  search(
    query: SearchQuery,
    signal: AbortSignal,
  ): readonly SearchResult[] | Promise<readonly SearchResult[]>;
}

/**
 * Parse raw input into a {@link SearchQuery}.
 *
 * A bare `/` is command mode with no terms, which is what makes it list every
 * command (需求 §4.2.1) — the same behaviour the old palette had on an empty
 * box, now reachable only under the prefix.
 */
export function parseSearchQuery(
  raw: string,
  tab: SearchTab,
  limit?: number,
  filters?: Readonly<Record<string, SearchFilterValue>>,
): SearchQuery {
  const trimmed = raw.trimStart();
  const isCommandMode = trimmed.startsWith(COMMAND_PREFIX);
  const body = isCommandMode ? trimmed.slice(COMMAND_PREFIX.length) : trimmed;
  return {
    raw,
    ...(isCommandMode ? { prefix: COMMAND_PREFIX } : {}),
    text: body.trim(),
    terms: body
      .toLowerCase()
      .split(/\s+/)
      .filter((term) => term.length > 0),
    tab,
    ...(limit === undefined ? {} : { limit }),
    ...(filters === undefined || Object.keys(filters).length === 0
      ? {}
      : { filters }),
  };
}

/**
 * Whether picking this row shows something beside it (so the face stays open)
 * rather than navigating away.
 */
export function isInPlaceResult(result: SearchResult): boolean {
  return result.action.type === 'command' && result.action.inPlace === true;
}

/**
 * The two commands whose whole job is to (re)open this same search face.
 *
 * Clicking them from the list must keep the dropdown up and apply their seed —
 * they are a mode switch, not a navigation. They are **not** {@link isInPlaceResult}:
 * that flag also arms a child-layer hold that only an overlay can clear, and
 * these two never open one (实测：标成 `inPlace` 之后 Esc 关不掉).
 */
export function isSearchFaceResult(result: SearchResult): boolean {
  return (
    result.action.type === 'command' &&
    (result.action.command === BUILTIN_COMMAND.commandPalette ||
      result.action.command === BUILTIN_COMMAND.search)
  );
}

/**
 * Whether picking this row should leave the dropdown open.
 *
 * In-place cards and the two search-face commands both stay; everything else
 * navigates and the stale list over the destination is worse.
 */
export function pickKeepsFaceOpen(result: SearchResult): boolean {
  return isInPlaceResult(result) || isSearchFaceResult(result);
}

/**
 * Close reasons that are a **child layer taking over**, not the user asking to
 * close the face: a filter popover's field taking the caret reads as
 * `focus-out`, a click inside it as `outside-press`, and Esc reaches both.
 */
const AMBIENT_CLOSE_REASONS: ReadonlySet<string> = new Set([
  'focus-out',
  'outside-press',
  'escape-key',
]);

/**
 * A `focus-out` that is not one: focus never ended up outside the face.
 *
 * Base UI reports the reason from the DOM event, but its handler runs a frame
 * or two later. Two situations make that gap a lie, and both were observed:
 *
 * - **Focus rests on nobody.** The focused node was *removed from the DOM*, so
 *   the browser drops focus to `<body>` for an instant and whatever owns the
 *   node puts it back on the next render. Completing a date range makes
 *   react-day-picker repaint its whole grid, replacing the focused day button.
 * - **Focus is back inside the face.** A closing child popover shuffles focus
 *   out to `<body>`, through the popup, and home to the omnibar input. By the
 *   time the outer dropdown is asked to close, focus is demonstrably *inside*
 *   it — you cannot have lost what you are holding.
 *
 * A real focus move is safe from this: Tab lands on some element outside, and
 * a click elsewhere also raises `outside-press`, which is judged separately —
 * a genuine outside click closes the face no matter where focus sits. A surface
 * iframe taking focus is also a real leave (`activeElement` is the frame, which
 * the face does not contain) — but that click never becomes `outside-press`, so
 * the host still has to close on the `focusin` the parent *does* see (见
 * {@link shouldCloseForSurfaceFrameFocus}).
 */
export function isPhantomFocusOut(
  reason: string,
  focusDidNotLeave: boolean,
): boolean {
  return reason === 'focus-out' && focusDidNotLeave;
}

/**
 * Whether focus is, right now, somewhere that means the face did **not** lose
 * it (見 {@link isPhantomFocusOut}) — on nobody, or back inside `face`.
 */
export function focusDidNotLeave(face?: HTMLElement | null): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  const active = document.activeElement;
  if (active === null || active === document.body) {
    return true;
  }
  return face?.contains(active) === true;
}

/**
 * Whether the face should **refuse** this close request.
 *
 * A pure function because it is the load-bearing bit and it has already
 * regressed once: the first attempt held only the ambient reasons, and missed
 * that **Base UI closes the popup itself when an item is pressed**
 * (`item-press`). With `open` controlled, declining to call `close()` does
 * nothing about that — so an in-place hit's card arrived over a dropdown that
 * had just vanished.
 *
 * Holding never traps the user: each reason is only held while a child layer is
 * actually up, and that layer consumes the same gesture. The first Esc (or
 * outside click) closes the child, which disarms this, so the second closes the
 * face normally.
 */
export function holdsFaceOpen(
  reason: string,
  state: {
    /** A filter popover / in-place layer this face spawned is up. */
    readonly childLayerOpen: boolean;
    /** The row about to be picked opens in place (见 {@link isInPlaceResult}). */
    readonly pickedInPlace: boolean;
    /**
     * The row reseeds this same face (见 {@link isSearchFaceResult}). Held on
     * `item-press` only — unlike `inPlace` it must not arm a child-layer hold.
     */
    readonly pickedKeepsOpen?: boolean;
    /** Focus never actually left the face (见 {@link isPhantomFocusOut}). */
    readonly focusDidNotLeave: boolean;
  },
): boolean {
  if (reason === 'item-press') {
    return state.pickedInPlace || state.pickedKeepsOpen === true;
  }
  // Held regardless of a child layer, and that is load-bearing: these arrive
  // *after* the child has closed and cleared `childLayerOpen`. Measured at
  // 20-40ms late, which is an eternity of renders — the child's own close is
  // what starts the focus shuffle that produces them.
  if (isPhantomFocusOut(reason, state.focusDidNotLeave)) {
    return true;
  }
  return state.childLayerOpen && AMBIENT_CLOSE_REASONS.has(reason);
}

/**
 * Whether `target` being focused means the user left the search face for a
 * surface iframe (见 09).
 *
 * A click inside an iframe never becomes Base UI `outside-press` (the pointer
 * lands in another document). It also does **not** dispatch `blur` / `focusout`
 * / `focusin` on the parent — Chromium just moves `document.activeElement` to
 * the frame. The host therefore watches `activeElement` while the face is
 * open, rather than waiting for an event that will not come. Held while a
 * child layer is up, same ambient-close hold as {@link holdsFaceOpen}.
 */
export function shouldCloseForSurfaceFrameFocus(
  target: EventTarget | null,
  childLayerOpen: boolean,
): boolean {
  return !childLayerOpen && target instanceof HTMLIFrameElement;
}

/**
 * The arguments to dispatch for a command action, with the row's rect appended
 * when the action asked to open in place.
 *
 * **Appended, not merged into the module's own first argument.** That object is
 * the module's shape — reaching into it would make the platform's envelope
 * collide with whatever key the module happens to use, and silently at that.
 * The reader is `readSearchHitAnchor`, which looks at the last argument.
 *
 * No anchor (the face could not measure the row) means no envelope: the module
 * then falls back to whatever it does without a position, which is the same
 * path its own UI takes.
 */
export function commandArgsWithAnchor(
  action: Extract<SearchAction, { type: 'command' }>,
  anchor: SearchHitAnchor | undefined,
): unknown[] {
  const args = [...(action.args ?? [])];
  if (action.inPlace && anchor) {
    args.push({ anchor } satisfies SearchHitAnchorEnvelope);
  }
  return args;
}

/** Whether a provider takes part in this query's tab and mode. */
export function providerAnswers(
  provider: SearchProvider,
  query: SearchQuery,
): boolean {
  if (query.tab.kind === 'app') {
    if (provider.moduleId !== query.tab.moduleId) {
      return false;
    }
    if (!scopeCovers(provider.scope, 'app')) {
      return false;
    }
  } else if (provider.moduleId !== undefined) {
    // The All tab carries the platform's own sections plus whatever a module
    // declared `scope: 'all'` for — content a user looks for without knowing
    // which app owns it (需求 §5.3.1 收窄).
    if (!scopeCovers(provider.scope, 'all')) {
      return false;
    }
    // …but only once something was typed. "Opening the box issues no
    // cross-process call" (需求 §4.3) is a hard promise, and an empty box
    // querying every all-scope module would spend it on nothing: none of them
    // can answer "" usefully anyway.
    if (query.text.length === 0) {
      return false;
    }
  }
  if (query.prefix === undefined) {
    // Prefix-less mode: everyone answers. A provider that only makes sense
    // under a prefix would have to opt out here, and none does yet.
    return true;
  }
  return (provider.prefixes ?? []).includes(query.prefix);
}
